<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { formataCnpj } from '@kodatec/brasil-utils';
import { usePessoasApi } from '@/modules/pessoas/composables/usePessoasApi';

const props = defineProps({
    modelValue: {
        type: Number,
        default: null,
    },
    id: {
        type: String,
        default: 'pessoa-juridica-campo',
    },
    titulo: {
        type: String,
        default: 'Pessoa Jurídica',
    },
    obrigatorio: {
        type: Boolean,
        default: true,
    },
    placeholder: {
        type: String,
        default: 'Selecione a pessoa jurídica',
    },
    erro: {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    // Carrega apenas os tipos de PJ informados (ids já resolvidos)
    tipos: {
        type: [Number, String, Array],
        default: null,
    },
    // Carrega os tipos de PJ a partir dos nomes (resolvidos internamente para ids)
    tiposNomes: {
        type: Array,
        default: null,
    },
    // Quando true, carrega apenas pessoas jurídicas estrangeiras
    estrangeiro: {
        type: Boolean,
        default: false,
    },
    // Predicado client-side opcional aplicado sobre a lista carregada
    filtro: {
        type: Function,
        default: null,
    },
    // Override: usa esta lista e ignora o carregamento interno
    options: {
        type: Array,
        default: null,
    },
    carregarAoMontar: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['update:modelValue', 'change']);

const {
    listaPessoasJuridicas,
    tiposPessoasJuridicas,
    carregarPessoasJuridicas,
    carregarPessoasJuridicasPorTipo,
    carregarPessoasJuridicasEstrangeiras,
    carregarTiposPessoasJuridicas,
} = usePessoasApi();

const carregando = ref(false);
const pessoaSelecionada = ref(null);

const opcoes = computed(() => {
    const base = props.options ?? listaPessoasJuridicas.value;
    return props.filtro ? base.filter(props.filtro) : base;
});

const carregarLista = async () => {
    if (props.options !== null) return; // lista fornecida externamente
    carregando.value = true;
    try {
        if (props.estrangeiro === true) {
            await carregarPessoasJuridicasEstrangeiras();
        } else if (props.tiposNomes?.length) {
            await carregarTiposPessoasJuridicas();
            const ids = props.tiposNomes
                .map((nome) => tiposPessoasJuridicas.value.find((tipo) => tipo.nome === nome)?.id)
                .filter((id) => id != null);
            await carregarPessoasJuridicasPorTipo(ids.join(','));
        } else if (props.tipos !== null) {
            await carregarPessoasJuridicasPorTipo(props.tipos);
        } else {
            await carregarPessoasJuridicas();
        }
    } finally {
        carregando.value = false;
    }
};

const sugestoes = ref([]);

const normalizarTexto = (texto) =>
    String(texto ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

// Filtra as opções carregadas pela razão social ou pelo CNPJ (com ou sem máscara)
const buscarPessoaJuridica = (event) => {
    const termo = normalizarTexto(event.query).trim();
    if (!termo) {
        sugestoes.value = [...opcoes.value];
        return;
    }
    const termoNumerico = termo.replace(/\D/g, '');
    sugestoes.value = opcoes.value.filter((pessoa) => {
        const porNome = normalizarTexto(pessoa.razao_social).includes(termo);
        const porCnpj = termoNumerico && String(pessoa.cnpj ?? '').replace(/\D/g, '').includes(termoNumerico);
        return porNome || porCnpj;
    });
};

const selecionarPessoa = (event) => {
    const pessoa = event?.value ?? null;
    if (pessoa && typeof pessoa === 'object') {
        emit('update:modelValue', pessoa.id);
        emit('change', pessoa);
    } else {
        emit('update:modelValue', null);
        emit('change', null);
    }
};

// Sincroniza o valor inicial (edição): localiza a PJ na lista pelo id e seleciona
const sincronizarSelecionada = () => {
    if (!props.modelValue) {
        if (pessoaSelecionada.value !== null) pessoaSelecionada.value = null;
        return;
    }
    if (pessoaSelecionada.value?.id === props.modelValue) return;
    const pessoa = opcoes.value.find((item) => item.id === props.modelValue);
    if (pessoa) {
        pessoaSelecionada.value = pessoa;
        emit('change', pessoa);
    }
};

watch([() => props.modelValue, opcoes], sincronizarSelecionada, { immediate: true });

// Recarrega a lista quando os filtros de tipo/estrangeiro mudam após a montagem
watch(
    [() => props.tipos, () => props.tiposNomes, () => props.estrangeiro],
    () => {
        if (props.carregarAoMontar) carregarLista();
    }
);

onMounted(() => {
    if (props.carregarAoMontar) carregarLista();
});

defineExpose({ carregarLista });
</script>

<template>
    <div class="field mb-2 col-12" :id="props.id">
        <SmallTitle :titulo="props.titulo" :obrigatorio="props.obrigatorio" />
        <AutoComplete v-model="pessoaSelecionada" :suggestions="sugestoes" forceSelection
            optionLabel="razao_social" inputClass="text-sm" :loading="carregando" :placeholder="props.placeholder"
            :disabled="props.disabled" :class="{ 'p-invalid': props.erro }" @complete="buscarPessoaJuridica"
            @change="selecionarPessoa">
            <template #option="slotProps">
                <div class="flex flex-column">
                    <p class="m-0 p-0 font-semibold text-sm">{{ slotProps.option.razao_social }}</p>
                    <span class="text-xs">{{ formataCnpj(slotProps.option.cnpj) }}</span>
                </div>
            </template>
        </AutoComplete>
        <SmallError :error="props.erro" />
    </div>
</template>
